// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { getCurrentSpan } from '@imprivata-cloud/common';
import { useCallback, useEffect } from 'react';

type TraceContext = {
  spanContext: string;
};

type ImprAgentApi = {
  window: {
    showWindow2: (
      workflowId: string,
      traceContext: TraceContext,
      value: Array<'hide' | 'show' | 'showmaximized'>,
    ) => void;
  };
  authn: {
    endWorkflowAndCloseWindow: (workflowId: string, traceContext: TraceContext) => void;
  };
};

declare global {
  interface Window {
    impr?: ImprAgentApi;
  }
}

function getTraceContext(): TraceContext {
  // Create B3 Descriptor
  let spanContext = '';
  const span = getCurrentSpan();
  if (span) {
    // https://github.com/openzipkin/b3-propagation#sampling-state-2
    const sampled = '1';
    // @ts-expect-error - Unknown
    const { traceId, spanId } = span._spanContext;
    spanContext = `${traceId as string}-${spanId as string}-${sampled as string}`;
  }

  const traceContext: TraceContext = {
    spanContext,
  };

  return traceContext;
}

/**
 * The uniqueAgentId name is used to differentiate it from the generated portal workflowId
 * and also to better represent the use of this value.
 * uniqueAgentId and workflowId are different things and should be named differently.
 */
const uniqueAgentId = new URLSearchParams(window.location.search).get('workflowId') || '';
const traceContext = getTraceContext();

/* Keep the agent logic isolated, do not separate it into different files. */
export default function useAgentWindow(closeEvents?: Array<keyof WindowEventMap> | Array<string>) {
  const showWindow = useCallback(() => {
    window.impr?.window?.showWindow2?.(uniqueAgentId, traceContext, ['show']);
  }, []);

  const hideWindow = useCallback(() => {
    window.impr?.window?.showWindow2?.(uniqueAgentId, traceContext, ['hide']);
  }, []);

  const endWorkflowAndCloseWindow = useCallback(() => {
    window.impr?.authn?.endWorkflowAndCloseWindow?.(uniqueAgentId, traceContext);
  }, []);

  useEffect(() => {
    if (!closeEvents) {
      return () => {};
    }

    showWindow();

    const handleUnload = () => {
      hideWindow();
      endWorkflowAndCloseWindow();
    };

    for (const event of closeEvents) {
      window.addEventListener(event, handleUnload);
    }

    return () => {
      for (const event of closeEvents) {
        window.removeEventListener(event, handleUnload);
      }
    };
  }, [closeEvents, endWorkflowAndCloseWindow, hideWindow, showWindow]);

  return { showWindow, hideWindow, endWorkflowAndCloseWindow };
}
